import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MailCheck, ShieldCheck, RefreshCw } from "lucide-react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";

// Founder-only: move the master admin to a new email, verify it, then scrub the old address.
export const AdminEmailMigrationCard = () => {
  const [st, setSt] = useState(null);
  const [form, setForm] = useState({ old_email: "", new_email: "admin@dhdh.ca" });
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => axios.get(`${API}/founder/admin-email/status`).then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const run = async (path, body, okMsg) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/founder/admin-email/${path}`, body);
      if (data.verification_sent === false) toast.warning("Saved, but the email could not be sent (SendGrid credits exceeded). Use Resend once credits are back.");
      else toast.success(okMsg);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  if (!st) return null;
  const pending = st.status === "pending_confirmation";
  return (
    <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5 mb-6" data-testid="admin-email-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-4 h-4 text-[#6B4E8A]" />
        <h3 className="font-medium text-[#6B4E8A]">Admin account email</h3>
        <span className="ml-auto text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F0ECF7] text-[#6B4E8A]" data-testid="admin-email-status">{st.status.replace("_", " ")}</span>
      </div>
      {st.current_admin_email && <p className="text-sm text-[#475569]" data-testid="admin-email-current">Current admin email: <strong>{st.current_admin_email}</strong>{st.old_alias_active ? " · old address still accepted for sign-in only" : ""}</p>}
      {st.status === "not_started" && (
        <div className="grid sm:grid-cols-3 gap-2 mt-3">
          <Input placeholder="Current (old) admin email" value={form.old_email} onChange={(e) => setForm({ ...form, old_email: e.target.value })} data-testid="admin-email-old-input" />
          <Input placeholder="New admin email" value={form.new_email} onChange={(e) => setForm({ ...form, new_email: e.target.value })} data-testid="admin-email-new-input" />
          <Button disabled={busy || !form.old_email || !form.new_email} onClick={() => run("start", form, "Admin email moved — verification email sent")} className="rounded-full bg-[#6B4E8A] text-white" data-testid="admin-email-start-btn">Move &amp; send verification</Button>
        </div>
      )}
      {pending && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-[#64748B]">
            Verification email: {st.verification_sent_at ? (st.verification_send_ok ? `sent ${new Date(st.verification_sent_at).toLocaleString("en-CA")}` : "NOT delivered (SendGrid refused it)") : "not sent"} · New address verified: <strong data-testid="admin-email-verified">{st.new_email_verified ? "yes" : "not yet"}</strong>
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm" disabled={busy} onClick={() => run("resend", {}, "Verification email sent")} className="rounded-full" data-testid="admin-email-resend-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" />Resend verification</Button>
            <label className="flex items-center gap-2 text-sm text-[#475569]">
              <Checkbox checked={confirm} onCheckedChange={(c) => setConfirm(!!c)} data-testid="admin-email-confirm-checkbox" />
              The email arrived and I can sign in with the new address
            </label>
            <Button size="sm" disabled={busy || !confirm || !st.new_email_verified} onClick={() => run("finalize", { confirm_login_works: true }, "Old address removed and admin sessions revoked")} className="rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="admin-email-finalize-btn"><MailCheck className="w-3.5 h-3.5 mr-1" />Remove old address</Button>
          </div>
        </div>
      )}
      {st.accounts_on_legacy_domain?.length > 0 && (
        <p className="mt-3 text-xs text-[#B45309]" data-testid="admin-email-legacy-list">Other accounts on the old domain: {st.accounts_on_legacy_domain.join(", ")}</p>
      )}
    </div>
  );
};

export default AdminEmailMigrationCard;
